import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { AuthProvider } from '@/shared/contexts/AuthContext';
import { TermProvider } from '@/shared/contexts/TermContext';
import { FeatureFlagProvider } from '@/shared/contexts/FeatureFlagContext';
import { initializeFeatureFlags } from '@/shared/services/featureFlagBootstrap';
import reportWebVitals from './reportWebVitals';
import './index.css';

const root = ReactDOM.createRoot(document.getElementById('root'));

// Started before the first render and deliberately not waited on. Nothing on
// the page is a feature flag's to delay: the login form, the home page and
// every finished feature are the same whichever way the flags resolve, so
// holding the first paint on /api/features would make an unrelated request's
// latency the whole application's time to render — up to the timeout in
// featureService, on every load, for every visitor.
//
// Not waiting is safe because it fails in the right direction. Every flag
// reads off until the server answers, and the flags reach components that are
// already mounted, so a gated feature appears a moment late rather than an
// unfinished one flashing up and disappearing. A gated route deep-linked in
// that window renders nothing until the flags land, which is the same blank
// page the old order showed for the whole request, and then resolves itself.
//
// It must stay on the line above root.render, not inside a .then or an effect.
// The call registers the flag provider synchronously and only then goes to the
// network; a hook that mounts before that registration is wired to an emitter
// the registration replaces, and would read off for the life of the page with
// nothing logged. See the comment in featureFlagBootstrap.js.
initializeFeatureFlags();

root.render(
    <React.StrictMode>
        <HelmetProvider>
            <BrowserRouter>
                {/*
                  Outside AuthProvider: /api/features is public and read before
                  anyone signs in, and what the app renders must not wait on a
                  session that may never exist.
                */}
                <FeatureFlagProvider>
                    <AuthProvider>
                        <TermProvider>
                            <App />
                        </TermProvider>
                    </AuthProvider>
                </FeatureFlagProvider>
            </BrowserRouter>
        </HelmetProvider>
    </React.StrictMode>
);

reportWebVitals();
